import React, { useState, useEffect } from 'react';
import api from './api';

const StockSplitting = () => {
    const [lots, setLots] = useState([]);
    const [selectedLot, setSelectedLot] = useState(null);
    
    // Form Inputs
    const [form, setForm] = useState({
        suffix: 'A',
        weight: '',
        rate: '',
        type: '',
        assortment: 'Normal' // Default string
    });

    // Preview Stats
    const [preview, setPreview] = useState(null);

    // 1. Fetch In-Stock Lots
    useEffect(() => {
        const fetchStock = async () => {
            const res = await api.get('/rough');
            // Only show lots with status 'Rough' and weight > 0
            const available = res.data.data.filter(l => l.status === 'Rough' && l.weight > 0);
            setLots(available);
        };
        fetchStock();
    }, []);

    // 2. Calculate Preview Live
    useEffect(() => {
        if (!selectedLot || !form.weight || !form.rate) {
            setPreview(null);
            return;
        }

        const splitW = parseFloat(form.weight);
        const splitR = parseFloat(form.rate);
        const splitVal = splitW * splitR;

        const remW = selectedLot.weight - splitW;
        const remVal = selectedLot.landedCost - splitVal;
        
        // Safety Check
        if (remW < 0 || remVal < 0) { // Allowed 0 for full split
            setPreview({ error: "Invalid Split: Weight exceeds available stock" });
            return;
        }

        const remRate = remW > 0 ? (remVal / remW) : 0;

        setPreview({
            childVal: splitVal,
            parentW: remW,
            parentVal: remVal,
            parentRate: remRate
        });

    }, [form, selectedLot]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (preview?.error) return;

        try {
            await api.post('/stock/split', {
                parentId: selectedLot._id,
                splitSuffix: form.suffix,
                splitWeight: form.weight,
                splitRate: form.rate,
                diamondType: form.type || selectedLot.diamondType,
                assortment: form.assortment
            });
            alert("✅ Split Successful!");
            window.location.reload(); // Quick refresh
        } catch (err) {
            alert("Error: " + err.response?.data?.error);
        }
    };

    return (
        <div className="container mt-4">
            <h4 className="fw-bold mb-4">Stock Operations: Splitting</h4>

            <div className="row">
                {/* LEFT: SOURCE SELECTION */}
                <div className="col-md-4">
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-dark text-white">1. Select Source Lot</div>
                        <div className="card-body">
                            <select className="form-select mb-3" onChange={(e) => {
                                const lot = lots.find(l => l._id === e.target.value);
                                setSelectedLot(lot);
                                setForm({
                                    ...form, 
                                    type: lot?.diamondType || '',
                                    assortment: lot?.assortment || '' // Pre-fill with parent's assortment
                                }); 
                            }}>
                                <option value="">-- Choose Lot --</option>
                                {lots.map(l => (
                                    <option key={l._id} value={l._id}>
                                        {l.rpNumber} ({l.weight} cts)
                                    </option>
                                ))}
                            </select>

                            {selectedLot && (
                                <div className="alert alert-secondary">
                                    <small className="text-muted d-block">Current Status:</small>
                                    <div className="d-flex justify-content-between fw-bold">
                                        <span>{selectedLot.weight} Cts</span>
                                        <span>@ {Math.round(selectedLot.costPerCarat)}</span>
                                    </div>
                                    <div className="text-center mt-2 text-primary fw-bold border-top pt-1">
                                        Val: {Math.round(selectedLot.landedCost).toLocaleString()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* MIDDLE: SPLIT DETAILS */}
                <div className="col-md-4">
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-primary text-white">2. New Packet Details</div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-2">
                                    <label className="small">New ID Suffix</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">{selectedLot?.rpNumber || 'RP-XX'}-</span>
                                        <input type="text" className="form-control" value={form.suffix} onChange={e => setForm({...form, suffix: e.target.value})} maxLength="5" placeholder="A" />
                                    </div>
                                </div>
                                <div className="row g-2 mb-2">
                                    <div className="col-6">
                                        <label className="small fw-bold">Weight</label>
                                        <input type="number" step="0.01" className="form-control" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})} required />
                                    </div>
                                    <div className="col-6">
                                        <label className="small fw-bold">New Rate</label>
                                        <input type="number" step="0.01" className="form-control" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} required />
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="small">Type / Shape</label>
                                    <input type="text" className="form-control" value={form.type} onChange={e => setForm({...form, type: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label className="small">Assortment</label>
                                    {/* CHANGED FROM SELECT TO INPUT */}
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={form.assortment} 
                                        onChange={e => setForm({...form, assortment: e.target.value})} 
                                        placeholder="e.g. Baguette, Rejection, Fancy"
                                    />
                                </div>
                                <button className="btn btn-primary w-100" disabled={!preview || preview.error}>Confirm Split</button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* RIGHT: PREVIEW */}
                <div className="col-md-4">
                    <div className="card shadow-sm">
                        <div className="card-header bg-success text-white">3. Result Preview</div>
                        <div className="card-body">
                            {!preview ? <p className="text-muted text-center py-4">Enter details to see calculation.</p> : (
                                <div>
                                    {preview.error ? (
                                        <div className="alert alert-danger">{preview.error}</div>
                                    ) : (
                                        <>
                                            <h6 className="fw-bold border-bottom pb-2">New Packet ({form.suffix})</h6>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>Value:</span>
                                                <span className="fw-bold">{Math.round(preview.childVal).toLocaleString()}</span>
                                            </div>

                                            <h6 className="fw-bold border-bottom pb-2 mt-4 text-secondary">Remaining Parent</h6>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>Weight:</span>
                                                <span className="fw-bold">{preview.parentW.toFixed(2)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>New Rate:</span>
                                                <span className="fw-bold text-danger">{Math.round(preview.parentRate).toLocaleString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between border-top pt-2">
                                                <span>Value:</span>
                                                <span className="fw-bold">{Math.round(preview.parentVal).toLocaleString()}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockSplitting;